import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { Shield, User, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { MFADialog } from '../components/MFADialog';

interface LoginForm {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export const LoginPage = () => {
  const { login, isLoading, mfaChallenge, requiresMFA, pendingCredentials } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMFADialog, setShowMFADialog] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  // Watch for MFA requirement changes in the store
  useEffect(() => {
    if (requiresMFA && mfaChallenge) {
      setShowMFADialog(true);
    }
  }, [requiresMFA, mfaChallenge]);

  const onSubmit = async (data: LoginForm) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      
      // Auto-lowercase username if it looks like an email
      const username = data.username.includes('@') ? data.username.toLowerCase() : data.username;
      
      await login({ 
        username, 
        password: data.password,
        rememberMe: data.rememberMe 
      });
      
      // MFA dialog will be shown automatically via useEffect when requiresMFA becomes true
    } catch (error: any) {
      // Error handling is done in the auth store
      console.error('Login failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFADialogClose = () => {
    setShowMFADialog(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-brand rounded-2xl flex items-center justify-center shadow-brand">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-gradient">VaultDrop</h1>
          <p className="mt-2 text-sm text-gray-600">
            Secure file upload system by <span className="font-medium text-brand-600">KEYper Systems</span>
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-lg rounded-xl border border-gray-100">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Sign in to your account</h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter your email address to access VaultDrop
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username or Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('username', {
                    required: 'Username is required',
                    minLength: {
                      value: 3,
                      message: 'Username must be at least 3 characters',
                    },
                  })}
                  type="text"
                  id="username"
                  autoComplete="username"
                  placeholder="Enter your username"
                  className={`input-field pl-10 ${errors.username ? 'border-error-500 focus:ring-error-500 focus:border-error-500' : ''}`}
                  disabled={isSubmitting || isLoading}
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-error-600">{errors.username.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`input-field pl-10 pr-10 ${errors.password ? 'border-error-500 focus:ring-error-500 focus:border-error-500' : ''}`}
                  disabled={isSubmitting || isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-error-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="w-full btn-primary py-3 text-base relative"
            >
              {(isSubmitting || isLoading) ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>


        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            Securely delivered by KEYper Systems • All uploads are encrypted and logged
          </p>
        </div>
      </div>

      {/* MFA Dialog */}
      {showMFADialog && pendingCredentials && mfaChallenge && (
        <MFADialog
          isOpen={showMFADialog}
          onClose={handleMFADialogClose}
          username={pendingCredentials.username}
          password={pendingCredentials.password}
          mfaChallenge={mfaChallenge}
          rememberMe={pendingCredentials.rememberMe}
        />
      )}
    </div>
  );
};